import { type FC, useState } from "react"
import { useDropzone } from "react-dropzone"
import styled from "styled-components"

import { type CoursePageAttachment } from "@forento/core/models/course"
import { getExtensionByFileName } from "@forento/core/utilities/file"

import Button, { SubmitButton } from "~/components/Button"
import Checkbox from "~/components/Checkbox"
import { AddIcon } from "~/components/Icon"
import InputField from "~/components/InputField"
import { DefaultModal, ModalButtons, ModalInputs, ModalTitle } from "~/components/Modal"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import useFileUpload from "~/hooks/useFileUpload"
import trpc from "~/utilities/trpc"

const AddAttachment: FC<{ pageId: number; onAdd(value: CoursePageAttachment): void }> = ({ pageId, onAdd }) => {
	const [selectedFile, setSelectedFile] = useState<File>()
	const [label, setLabel] = useState("")
	const [isListedInCourse, setListedInCourse] = useState(true)
	const [isSubmitting, setSubmitting] = useState(false)

	const fileUpload = useFileUpload()

	const isValid = label.trim().length > 0 && selectedFile !== undefined

	const handleDrop = async (acceptedFiles: File[]) => {
		if (acceptedFiles.length === 0) return
		setSelectedFile(acceptedFiles[0])
	}

	const handleSubmit = async () => {
		if (!isValid) return
		setSubmitting(true)
		try {
			const uploaded = await fileUpload.upload({
				file: selectedFile,
				kind: "course-page-attachment",
				fileExtension: getExtensionByFileName(selectedFile.name) ?? undefined,
			})
			if (uploaded === null) {
				setSubmitting(false)
				return
			}
			const attachment = await trpc.course.createPageAttachment.mutate({
				pageId,
				label,
				fileName: uploaded.fileName,
				isListedInCourse,
			})
			onAdd(attachment)
			setSelectedFile(undefined)
			setLabel("")
		} catch (error) {
			console.error(error)
			setSubmitting(false)
		}
		setSubmitting(false)
	}

	const dropzone = useDropzone({ onDrop: handleDrop })

	return (
		<>
			<DefaultModal
				onClose={() => setSelectedFile(undefined)}
				isOpen={selectedFile !== undefined}
				onSubmit={handleSubmit}
			>
				<ModalTitle>Add attachment</ModalTitle>
				<ModalInputs>
					<InputField label="Label" value={label} onChange={setLabel} />
					<Checkbox
						label="Include in course resources"
						isChecked={isListedInCourse}
						onChange={setListedInCourse}
					/>
				</ModalInputs>
				<ModalButtons>
					<Button variant="secondary" onClick={() => setSelectedFile(undefined)} isDisabled={isSubmitting}>
						Cancel
					</Button>
					<SubmitButton variant="primary" isLoading={isSubmitting} isDisabled={!isValid}>
						Add
					</SubmitButton>
				</ModalButtons>
			</DefaultModal>
			<Container {...dropzone.getRootProps()}>
				<input {...dropzone.getInputProps()} />
				{getContent({
					isUploading: isSubmitting,
					isDragActive: dropzone.isDragActive,
					isDragReject: dropzone.isDragReject,
				})}
			</Container>
		</>
	)
}

const getContent = ({
	isUploading,
	isDragActive,
	isDragReject,
}: {
	isUploading: boolean
	isDragActive: boolean
	isDragReject: boolean
}) => {
	if (isUploading) return <PartialLoadingPage />
	if (isDragActive)
		return <InfoText>{isDragReject ? "That file type is not supported" : `Drop here to add attachment`}</InfoText>

	return (
		<>
			<StyledAddIcon />
			<InfoText>Add attachment</InfoText>
		</>
	)
}

const Container = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	border: 1px dashed ${props => props.theme.primaryColor};
	border-radius: 8px;
	padding: 8px;
	font-weight: 500;
	font-size: 16px;
	cursor: pointer;
`

const StyledAddIcon = styled(AddIcon)`
	width: 24px;
	height: 24px;
`

const InfoText = styled.p`
	font-weight: 500;
	font-size: 16px;
`

export default AddAttachment
